import { useMemo, useEffect, useRef } from 'react';
import { add, divide, multiply, norm, subtract } from 'mathjs';
import {
  DefaultShapeStyle,
  HoveredShapeStyle,
  SelectedShapeStyle,
} from '../SheetsViewerV2.config';
import {
  AbstractSheetFilledShape,
  SheetFillStyleSet,
} from './abstract/AbstractSheetFilledShape';
import { ActiveSheetData } from 'src/components/viewers/SheetsViewerV2/hooks/useActiveSheet';
import { SheetShapeStyleType } from 'src/components/viewers/SheetsViewerV2/shapes/abstract/AbstractSheetShape';
import { SheetShapeDeepFragment } from 'src/gql/graphql';
import {
  GetSparkelPropertiesForProjectQuery,
  SheetScaleType,
} from 'src/gql/graphql';
import { useTaperedInsulation } from 'src/components/orderContractor/pdf-shapes/protan/useTaperedInsulation';
import { Substrate } from 'src/components/orderContractor/ProductChoices';
import {
  getCrossSectionFromProperties,
  getProductAreasFromProperties,
  getRoofShapeFromProperties,
  getRoofUValueFromProperties,
} from 'src/components/orderContractor/pdf-shapes/SheetDrainLineDrawing';
import { useUserTenant } from 'src/services/auth-info';
import { useOrderLabelsProtan } from 'src/hooks/protan-data';
import { Point2 } from 'src/domain/geometry/geometric-types';
import Edit2D = Autodesk.Extensions.Edit2D;
import GuiViewer3D = Autodesk.Viewing.GuiViewer3D;
import Viewer3D = Autodesk.Viewing.Viewer3D;
import { ChakraColor } from 'src/utils/color-util';

// The actual shape class that handles rendering
class TaperedInsulationShapeClass extends AbstractSheetFilledShape {
  private propertyData?: GetSparkelPropertiesForProjectQuery;
  private calibration?: SheetScaleType;
  private renderedSheetShapes?: Record<string, SheetShapeDeepFragment[]>;

  constructor(
    viewer: Viewer3D | GuiViewer3D,
    edit2d: Edit2D,
    activeSheetData: ActiveSheetData,
    shapeModel?: SheetShapeDeepFragment,
    line?: THREE.Vector3[],
    splitLineStyleSet?: SheetFillStyleSet,
    renderedSheetShapes?: Record<string, SheetShapeDeepFragment[]>,
    unitScale?: number,
    propertyData?: GetSparkelPropertiesForProjectQuery,
    calibration?: SheetScaleType,
    crossSectionPolygons?: any[],
    planSlices?: any[]
  ) {
    super(viewer, edit2d, activeSheetData, shapeModel);

    this.propertyData = propertyData;
    this.calibration = calibration;
    this.renderedSheetShapes = renderedSheetShapes;

    const convertedLine = this.getConvertedLine(shapeModel, line);
    this.validateInputs(shapeModel, line, convertedLine);

    // Create split line shapes with their own style
    this.createSplitLineShapes(convertedLine || [], splitLineStyleSet);

    // Add polygon shapes for tapered insulation if available
    if (crossSectionPolygons && crossSectionPolygons.length > 0) {
      // Limit the number of polygons to prevent freezing (increased limit)
      const maxPolygons = 1000; // Increased limit for better visualization
      const limitedPolygons = crossSectionPolygons.slice(0, maxPolygons);

      if (crossSectionPolygons.length > maxPolygons) {
        console.warn(
          `TaperedInsulationShapeClass: Limiting polygons from ${crossSectionPolygons.length} to ${maxPolygons} to prevent freezing`
        );
      }

      this.addPolygonShapes(limitedPolygons);
    }

    if (planSlices && planSlices.length > 0) {
      // Limit the number of plan slices to prevent freezing (increased limit)
      const maxSlices = 500; // Increased limit for better visualization
      const limitedSlices = planSlices.slice(0, maxSlices);

      if (planSlices.length > maxSlices) {
        console.warn(
          `TaperedInsulationShapeClass: Limiting plan slices from ${planSlices.length} to ${maxSlices} to prevent freezing`
        );
      }

      limitedSlices.forEach((slice, index) => {
        if (slice.polygons) {
          // Also limit polygons within each slice (increased limit)
          const maxSlicePolygons = 200;
          const limitedSlicePolygons = slice.polygons.slice(
            0,
            maxSlicePolygons
          );

          if (slice.polygons.length > maxSlicePolygons) {
            console.warn(
              `TaperedInsulationShapeClass: Limiting polygons in slice ${index} from ${slice.polygons.length} to ${maxSlicePolygons}`
            );
          }

          this.addPolygonShapesWithColor(limitedSlicePolygons, slice.color);
        }
      });
    }

    // Set the default style set for polygons (orange colors)
    this.styleSet = {
      default: {
        ...DefaultShapeStyle,
        fillColor: ChakraColor.orange100,
        lineColor: ChakraColor.orange300,
        fillAlpha: 0.3,
      },
      hovered: {
        ...HoveredShapeStyle,
        fillColor: ChakraColor.orange100,
        lineColor: ChakraColor.orange300,
        fillAlpha: 0.3,
      },
      selected: SelectedShapeStyle,
    };

    this.setStyle(SheetShapeStyleType.default);
  }

  private getConvertedLine(
    shapeModel?: SheetShapeDeepFragment,
    line?: THREE.Vector3[]
  ): { x: number; y: number }[] | undefined {
    if (shapeModel?.sheetShapeSplitLine) {
      return shapeModel.sheetShapeSplitLine.points.map((point) =>
        super.convertPointToRender(point)
      );
    }
    return line?.map((point) => ({ x: point.x, y: point.y }));
  }

  private validateInputs(
    shapeModel?: SheetShapeDeepFragment,
    line?: THREE.Vector3[],
    convertedLine?: { x: number; y: number }[]
  ) {
    if (shapeModel && !shapeModel.sheetShapeSplitLine) {
      throw new Error('shapeModel.sheetShapeSplitLine must be defined');
    } else if (!shapeModel && !Array.isArray(line)) {
      throw new Error('One shapeModel or line must be passed');
    } else if (!convertedLine) {
      throw new Error('Cannot define line');
    }
  }

  private createSplitLineShapes(
    convertedLine: { x: number; y: number }[],
    splitLineStyleSet?: SheetFillStyleSet
  ) {
    const textLabelPoints: { x: number; y: number }[] = [];

    // Create line segments for each pair of points
    for (let i = 0; i < convertedLine.length - 1; i += 2) {
      const startPoint = convertedLine[i];
      const endPoint = convertedLine[i + 1];

      if (startPoint && endPoint) {
        // Create a line segment from this pair of points using Polyline
        const lineSegment = new Autodesk.Edit2D.Polyline([
          startPoint,
          endPoint,
        ]);

        // Set individual style for this split line shape
        if (splitLineStyleSet) {
          this.setShapeStyle(lineSegment, splitLineStyleSet);
        }

        this.shapes.push(lineSegment);

        // Add a text label point at the midpoint of this segment
        const midPoint = {
          x: (startPoint.x + endPoint.x) / 2,
          y: (startPoint.y + endPoint.y) / 2,
        };
        textLabelPoints.push(midPoint);
      }
    }

    this.textLabelPoints = textLabelPoints;
  }

  private addPolygonShapes(polygons: any[]) {
    polygons.forEach((polygon, index) => {
      if (polygon.exterior && polygon.exterior.length >= 3) {
        // Convert points using the coordinate conversion method
        const convertedPoints = polygon.exterior.map(
          (point: number[], pointIndex: number) => {
            return super.convertPointToRender({ x: point[0], y: point[1] });
          }
        );

        // Convert to THREE.Vector3 for the Autodesk shape
        const vectorPoints = convertedPoints.map(
          (point: { x: number; y: number }) =>
            new THREE.Vector3(point.x, point.y, 0)
        );

        const polygonShape = new Autodesk.Edit2D.Polygon(vectorPoints);
        this.shapes.push(polygonShape);
      }
    });
  }

  private addPolygonShapesWithColor(polygons: any[], color: string) {
    polygons.forEach((polygon) => {
      if (polygon.exterior && polygon.exterior.length >= 3) {
        // Convert points using the coordinate conversion method
        const convertedPoints = polygon.exterior.map((point: number[]) =>
          super.convertPointToRender({ x: point[0], y: point[1] })
        );

        // Convert to THREE.Vector3 for the Autodesk shape
        const vectorPoints = convertedPoints.map(
          (point: { x: number; y: number }) =>
            new THREE.Vector3(point.x, point.y, 0)
        );

        const polygonShape = new Autodesk.Edit2D.Polygon(vectorPoints);

        // Create a custom style set for this polygon with the specified color
        const customStyleSet: SheetFillStyleSet = {
          default: {
            ...DefaultShapeStyle,
            fillColor: color,
            lineColor: color,
            fillAlpha: 0.3,
          },
          hovered: {
            ...HoveredShapeStyle,
            fillColor: color,
            lineColor: color,
            fillAlpha: 0.3,
          },
          selected: SelectedShapeStyle,
        };

        // Set the individual style for this polygon
        this.setShapeStyle(polygonShape, customStyleSet);

        this.shapes.push(polygonShape);
      }
    });
  }

  public hitTest(x: number, y: number) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return this.shapes.some((shape) => shape.hitTest(x, y, 0.1));
  }

  // Override getLabels to return tapered insulation labels with proper positioning
  public getLabels(options?: { fontSize?: number }): any {
    if (!this.apiModel?.sheetShapeSplitLine || !this.propertyData) {
      const baseLabels = super.getLabels(options);
      return baseLabels.map((label) => ({
        ...label,
        fontSize: options?.fontSize || 12,
      }));
    }

    // Get cross-section data
    const crossSection = getCrossSectionFromProperties(
      this.propertyData,
      this.apiModel.dbId,
      this.apiModel.urn
    );

    if (!crossSection) {
      const baseLabels = super.getLabels(options);
      return baseLabels.map((label) => ({
        ...label,
        fontSize: options?.fontSize || 12,
      }));
    }

    // Get U-value
    const uValue = getRoofUValueFromProperties(
      this.propertyData,
      this.apiModel.dbId,
      this.apiModel.urn
    );

    // Calculate single label position similar to the old viewer
    const baseLineDirection = subtract(
      crossSection.baseLine[1],
      crossSection.baseLine[0]
    ) as Point2;

    const tangentVector = divide(
      baseLineDirection,
      norm(baseLineDirection)
    ) as Point2;

    const normalVector: Point2 = [tangentVector[1], -tangentVector[0]];

    const labelPosition = add(
      crossSection.baseLine[0],
      multiply(normalVector, 50)
    ) as Point2;

    // Convert position to viewer coordinates
    const convertedPosition = super.convertPointToRender({
      x: labelPosition[0],
      y: labelPosition[1],
    });

    const labels: {
      point: THREE.Vector3;
      label: string;
      rotation?: number;
      fontSize: number;
    }[] = [];

    // Create a single multi-line label
    const labelLines: string[] = [];
    labelLines.push(`Min.: ${crossSection.minHeight * 1000} mm`);

    if (uValue) {
      const roundedUValue = uValue.toFixed(2);
      labelLines.push(`U-value: ${roundedUValue} W/m²K`);
    }

    // Join lines with newline character to create a single multi-line label
    const multiLineLabel = labelLines.join('\n');

    labels.push({
      point: this.viewer.worldToClient(
        new THREE.Vector3(convertedPosition.x, convertedPosition.y, 0)
      ),
      label: multiLineLabel,
      fontSize: options?.fontSize || 12,
    });

    return labels;
  }
}

// React functional component that uses the hook
interface TaperedInsulationShapeProps {
  viewer: Viewer3D | GuiViewer3D;
  edit2d: Edit2D;
  activeSheetData: ActiveSheetData;
  shapeModel?: SheetShapeDeepFragment;
  line?: THREE.Vector3[];
  styleSet?: SheetFillStyleSet;
  renderedSheetShapes?: Record<string, SheetShapeDeepFragment[]>;
  unitScale?: number;
  propertyData?: GetSparkelPropertiesForProjectQuery;
  calibration?: SheetScaleType;
  onShapeCreated?: (shape: TaperedInsulationShapeClass) => void;
}

function TaperedInsulationShapeComponent({
  viewer,
  edit2d,
  activeSheetData,
  shapeModel,
  line,
  styleSet,
  renderedSheetShapes,
  unitScale,
  propertyData,
  calibration,
  onShapeCreated,
}: TaperedInsulationShapeProps) {
  const shapeRef = useRef<TaperedInsulationShapeClass | null>(null);
  const { isProtan } = useUserTenant();
  const { substrate } = useOrderLabelsProtan();

  // Get tapered insulation data
  const taperedInsulationData = useMemo(() => {
    if (!isProtan || !shapeModel || !propertyData) {
      return {
        crossSection: null,
        roofShapeDbId: undefined,
        currentProductAreas: {},
        currentRoofUValue: null,
      };
    }

    const crossSection = getCrossSectionFromProperties(
      propertyData,
      shapeModel.dbId,
      shapeModel.urn
    );

    const roofShapeDbId = getRoofShapeFromProperties(
      propertyData,
      shapeModel.dbId,
      shapeModel.urn
    );

    const currentProductAreas = getProductAreasFromProperties(
      propertyData,
      shapeModel.dbId,
      shapeModel.urn,
      substrate ?? ''
    );

    const currentRoofUValue = getRoofUValueFromProperties(
      propertyData,
      shapeModel.dbId,
      shapeModel.urn
    );

    return {
      crossSection,
      roofShapeDbId,
      currentProductAreas,
      currentRoofUValue,
    };
  }, [isProtan, shapeModel, propertyData, substrate]);

  // Use the tapered insulation hook to get polygon data only when we have valid cross section data
  const crossSectionForHook = taperedInsulationData.crossSection || {
    baseLine: [
      [0, 0],
      [1, 0],
    ] as [Point2, Point2],
    slope: 0.025,
    lowPoints: [0],
    minHeight: 260,
  };

  const { crossSectionPolygons, planSlices } = useTaperedInsulation({
    dbId: shapeModel?.dbId || 0,
    crossSection: crossSectionForHook,
    currentProductAreas: taperedInsulationData?.currentProductAreas || {},
    currentRoofUValue: taperedInsulationData?.currentRoofUValue || null,
    roofShapeDbId: taperedInsulationData?.roofShapeDbId || undefined,
    substrate: (substrate as Substrate) || 'Concrete',
  });

  // Create the main shape instance
  // eslint-disable-next-line complexity
  useEffect(() => {
    if (!viewer || !edit2d || !activeSheetData) {
      return;
    }

    // For drain line shapes, we might want to disable polygon generation entirely
    // since they can generate thousands of polygons and cause freezing
    const isDrainLineShape = !!shapeModel?.sheetShapeSplitLine;

    const shouldAddPolygons =
      isProtan &&
      taperedInsulationData?.crossSection &&
      taperedInsulationData?.roofShapeDbId &&
      substrate &&
      !isDrainLineShape; // Disable for drain line shapes

    const shape = new TaperedInsulationShapeClass(
      viewer,
      edit2d,
      activeSheetData,
      shapeModel,
      line,
      styleSet,
      renderedSheetShapes,
      unitScale,
      propertyData,
      calibration,
      shouldAddPolygons ? crossSectionPolygons : undefined,
      shouldAddPolygons ? planSlices || [] : undefined
    );

    shapeRef.current = shape;

    // Draw the shape to add it to the layer
    shape.draw();

    if (onShapeCreated) {
      onShapeCreated(shape);
    }

    return () => {
      if (shapeRef.current) {
        shapeRef.current.remove();
        shapeRef.current = null;
      }
    };
  }, [
    viewer,
    edit2d,
    activeSheetData,
    shapeModel,
    line,
    styleSet,
    renderedSheetShapes,
    unitScale,
    propertyData,
    calibration,
    onShapeCreated,
    isProtan,
    taperedInsulationData,
    substrate,
    crossSectionPolygons,
    planSlices,
  ]);

  // Return null since this component doesn't render anything visible
  return null;
}

// Export the class for direct instantiation
export { TaperedInsulationShapeClass };
// Export the React component as default
export default TaperedInsulationShapeComponent;
